import React, { useState, useMemo } from "react";
import {
  Row,
  Col,
  Container,
  Button,
  Dropdown,
  OverlayTrigger,
  Tooltip,
} from "react-bootstrap";
import { ViewPractice } from "../Components/Practice/viewpractice";
import { ViewUsers } from "../Components/Users/ViewUser";
import { AddUser } from "../Components/Users/AddUser";
import { ViewServices } from "../Components/Services/viewServices";
import { SetUpPractice } from "../Components/Practice/SetupPractice";
import TemplateManagement from "../Components/Templates/TemplateManagement";
import { AddTemplate } from "../Components/Templates/AddTemplate";
import { BankingPayments } from "../Components/BankingPayments/BankingPayments";
import { AddBanking } from "../Components/BankingPayments/AddBankingPayments";
import { Comms } from "../Components/Communications/Comms";
import { ViewOnlineBookings } from "../Components/Practice/ViewOnlineBookings";
import { ViewPeople } from "../Components/People/ViewPeople";
import { ViewIntakeSettings } from "../Components/Practice/ViewIntakeSettings";
import ViewAppointmentSettings from "../Components/Practice/ViewAppointmentSettings";
import WhatsAppSettings from "../Components/WhatsApp/WhatsAppSettings";
import { userCanUseWhatsApp } from "../Components/WhatsApp/access";
import "../Components/Accounts/acc-rail.css";


export function Practice() {
  const [activeSection, setActiveSection] = useState("practice");
  const [refresh, setRefresh] = useState(false);
  const [addServiceTrigger, setAddServiceTrigger] = useState(0);
  const [addPeopleTrigger, setAddPeopleTrigger] = useState(0);
  // Tracks which rail item's submenu popover is currently open. Keyed by
  // `${prefix}-${item.key}` so the mobile-strip popover and desktop-rail
  // popover never interfere even though both render simultaneously.
  const [openSubKey, setOpenSubKey] = useState(null);
  const handleRefresh = () => setRefresh((prev) => !prev);

  // WhatsApp is gated by the practice's subscription tier (Connected and above).
  // The JWT carries the resolved answer; practiceRoutes/whatsappRoutes re-check
  // the practice's live tier on every request, so this is presentation only.
  const canUseWhatsApp = useMemo(() => userCanUseWhatsApp(), []);

  const sidebarItems = useMemo(
    () => [
      { key: "practice", label: "Practice Information", short: "Practice", icon: "fa-light fa-hospital" },
      { key: "users", label: "User Management", short: "Users", icon: "fa-sharp fa-light fa-users" },
      { key: "services", label: "Services & Custom Codes", short: "Services", icon: "fa-sharp fa-light fa-list-check" },
      { key: "banking", label: "Banking & Payments", short: "Banking", icon: "fa-sharp fa-light fa-building-columns" },
      {
        key: "reminders",
        label: "Communications",
        short: "Comms",
        icon: "fa-sharp fa-light fa-comments",
        children: [
          // `fa-brands` is required: `whatsapp` exists only in the Brands
          // family, so a bare `fa` (which resolves to the kit's default style)
          // finds no glyph. It also needs the Brands style enabled in the
          // FontAwesome kit — the class alone is not enough, and a kit without
          // it renders this blank while every other icon here works, because
          // nothing else in the app uses Brands.
          ...(canUseWhatsApp
            ? [{ key: "whatsapp", label: "WhatsApp", icon: "fa-brands fa-whatsapp" }]
            : []),
          { key: "sms", label: "SMS Reminders", icon: "fa-light fa-comment-sms" },
          { key: "email", label: "Email Reminders", icon: "fa-light fa-envelope" },
          { key: "accounts", label: "Financial Emails", icon: "fa-light fa-file-invoice-dollar" },
          { key: "reports", label: "Report Emails", icon: "fa-light fa-chart-line" },
        ],
      },
      { key: "templates", label: "Template Management", short: "Templates", icon: "fa-sharp fa-light fa-file-lines" },
      { key: "people", label: "People Management", short: "People", icon: "fa-light fa-address-book" },
      {
        key: "bookings",
        label: "Bookings",
        short: "Bookings",
        icon: "fa-sharp fa-light fa-calendar-check",
        children: [
          { key: "appointments", label: "Appointment Settings", icon: "fa-light fa-calendar-day" },
          { key: "calendar", label: "Online Bookings", icon: "fa-light fa-calendar-check" },
        ],
      },
      { key: "intake", label: "Intake Form", short: "Intake", icon: "fa-light fa-clipboard-user" },
    ],
    [canUseWhatsApp],
  );

  // A grouped rail item is never itself the active section — only its children
  // are — so the parent has to claim its children to stay highlighted. Derived
  // from sidebarItems rather than a second hardcoded list, which is how the old
  // COMMS_KEYS constant would have rotted once a second group existed.
  const parentOfChild = useMemo(() => {
    const map = {};
    for (const item of sidebarItems) {
      for (const child of item.children || []) map[child.key] = item;
    }
    return map;
  }, [sidebarItems]);

  const isItemActive = (item) => {
    if (activeSection === item.key) return true;
    return parentOfChild[activeSection]?.key === item.key;
  };

  const handleSubClick = (sub) => {
    setActiveSection(sub.key);
    setOpenSubKey(null);
  };

  const activeLabel = useMemo(() => {
    const parent = parentOfChild[activeSection];
    if (parent) return parent.label;
    return sidebarItems.find((i) => i.key === activeSection)?.label || "";
  }, [activeSection, sidebarItems, parentOfChild]);

  function renderRightSlot() {
    switch (activeSection) {
      case "practice":
        return <SetUpPractice onPracticeUpdate={handleRefresh} />;
      case "users":
        return <AddUser onUserUpdate={handleRefresh} />;
      case "templates":
        return <AddTemplate onTemplateUpdate={handleRefresh} />;
      case "banking":
        return <AddBanking onBankingUpdate={handleRefresh} />;
      case "people":
        return (
          <Button
            variant="primary"
            onClick={() => setAddPeopleTrigger((n) => n + 1)}
          >
            Add Person
          </Button>
        );
      default:
        return null;
    }
  }

  function renderContent() {
    switch (activeSection) {
      case "practice":
        return (
          <ViewPractice refresh={refresh} onPracticeUpdate={handleRefresh} />
        );
      case "users":
        return <ViewUsers refresh={refresh} onUserUpdate={handleRefresh} />;
      case "services":
        return (
          <ViewServices
            refresh={refresh}
            onServiceUpdate={handleRefresh}
            addTrigger={addServiceTrigger}
          />
        );
      case "templates":
        return (
          <TemplateManagement refresh={refresh} onTemplateEdit={handleRefresh} />
        );
      case "banking":
        return (
          <BankingPayments refresh={refresh} onBankingUpdate={handleRefresh} />
        );
      case "calendar":
        return (
          <ViewOnlineBookings
            refresh={refresh}
            onOnlineBookingUpdate={handleRefresh}
          />
        );
      case "sms":
      case "email":
      case "accounts":
      case "reports":
        return <Comms initialTab={activeSection} />;
      case "appointments":
        return (
          <ViewAppointmentSettings refresh={refresh} onUpdate={handleRefresh} />
        );
      case "whatsapp":
        return <WhatsAppSettings />;
      case "intake":
        return <ViewIntakeSettings refresh={refresh} onUpdate={handleRefresh} />;
      case "people":
        return (
          <ViewPeople
            refresh={refresh}
            onPeopleUpdate={handleRefresh}
            addTrigger={addPeopleTrigger}
          />
        );
      default:
        return <ViewPractice />;
    }
  }

  // Renders one rail icon. Items with children use a Bootstrap Dropdown
  // (compact dropdown-item styling via `actions-dropdown`, matching the
  // Accounts / PostedInvoice Actions menu). Items without children use a
  // tooltip on mobile only; desktop stays icon-only to match billing.jsx.
  function renderRailItem(item, { prefix, tooltipPlacement, popPlacement }) {
    const isActive = isItemActive(item);
    const hasChildren = !!item.children?.length;
    const submenuKey = `${prefix}-${item.key}`;

    if (hasChildren) {
      const dropDirection = popPlacement === "right" ? "end" : "down";
      const menuAlign = popPlacement === "right" ? "start" : "end";
      return (
        <Dropdown
          key={submenuKey}
          drop={dropDirection}
          align={menuAlign}
          show={openSubKey === submenuKey}
          onToggle={(show) => setOpenSubKey(show ? submenuKey : null)}
        >
          <Dropdown.Toggle
            as={Button}
            aria-label={item.label}
            className={
              isActive
                ? "acc-rail-item acc-rail-item-labeled active"
                : "acc-rail-item acc-rail-item-labeled"
            }
          >
            <i className={item.icon} />
            <span className="acc-rail-label">{item.short}</span>
          </Dropdown.Toggle>
          <Dropdown.Menu
            className="shadow-sm actions-dropdown"
            renderOnMount
            popperConfig={{
              strategy: "fixed",
              modifiers: [
                {
                  name: "computeStyles",
                  options: { adaptive: false, gpuAcceleration: false },
                },
              ],
            }}
          >
            {item.children.map((sub) => (
              <Dropdown.Item
                key={sub.key}
                active={activeSection === sub.key}
                onClick={() => handleSubClick(sub)}
              >
                {sub.icon && <i className={`${sub.icon} me-2`} />}
                {sub.label}
              </Dropdown.Item>
            ))}
          </Dropdown.Menu>
        </Dropdown>
      );
    }

    const buttonNode = (
      <Button
        aria-label={item.label}
        className={
          isActive
            ? "acc-rail-item acc-rail-item-labeled active"
            : "acc-rail-item acc-rail-item-labeled"
        }
        onClick={() => setActiveSection(item.key)}
      >
        <i className={item.icon} />
        <span className="acc-rail-label">{item.short}</span>
      </Button>
    );

    if (tooltipPlacement) {
      return (
        <OverlayTrigger
          key={submenuKey}
          placement={tooltipPlacement}
          overlay={
            <Tooltip
              id={`tip-${submenuKey}`}
              className="acc-rail-tip"
            >
              {item.label}
            </Tooltip>
          }
        >
          {buttonNode}
        </OverlayTrigger>
      );
    }

    return React.cloneElement(buttonNode, { key: submenuKey });
  }

  const rightSlot = renderRightSlot();

  return (
    <div style={{ marginTop: "50px" }}>
      <Container fluid className="px-0">
        <Row className="g-0">
          {/* MOBILE TAB STRIP (<768px) — horizontal-scroll icon row */}
          <Col xs={12} className="d-flex d-md-none g-0">
            <Container className="bg-white g-0 shadow-sm rounded">
              <div className="acc-rail-mobile">
                {sidebarItems.map((item) =>
                  renderRailItem(item, {
                    prefix: "mobile",
                    tooltipPlacement: "top",
                    popPlacement: "bottom",
                  })
                )}
              </div>
            </Container>
          </Col>

          {/* LEFT PANEL — DESKTOP ICON RAIL (>=768px) */}
          <Col xs="auto" className="d-none d-md-block g-0" style={{ width: 64 }}>
            <Container className="bg-white vh-100 g-0 p-1 shadow-sm rounded" style={{ width: 64 }}>
              <div className="acc-rail">
                {sidebarItems.map((item) =>
                  renderRailItem(item, {
                    prefix: "desktop",
                    tooltipPlacement: null,
                    popPlacement: "right",
                  })
                )}
              </div>
            </Container>
          </Col>

          {/* RIGHT CONTENT */}
          <Col xs={12} md className="g-0">
            <Container fluid className="pt-3 bg-transparent pb-3 mb-3 rounded">
              <Row className="align-items-center mb-3 g-0">
                <Col>
                  <h4 className="text-primary fw-normal mb-0 ms-1">
                    {activeLabel}
                  </h4>
                </Col>
                {rightSlot && (
                  <Col xs="auto" className="text-end">
                    {rightSlot}
                  </Col>
                )}
              </Row>
              {renderContent()}
            </Container>
          </Col>
        </Row>
      </Container>
    </div>
  );
}
